/**
 * ChatWebSocketContext - 채팅 웹소켓 공유 컨텍스트
 *
 * 한 창(window)에 소켓 하나. MainLayout 과 ChatPopupPage(별도 창) 가 각각 Provider 를 둔다.
 *
 * 설계(2026-10-03 개편): 수신 메시지는 훅(useChatWebSocket)이 React Query 캐시
 * (["chat-messages", roomId] / ["chat-rooms"]) 에 직접 반영한다. 컴포넌트는 쿼리 데이터만 읽고,
 * 컨텍스트는 연결 상태·타이핑·읽음·전송 함수·원시 프레임 구독만 제공한다.
 * (이전의 messages 평면 배열·addMessage/clearMessages/deletedMessageIds 병행 저장소는 폐기 —
 *  방 목록이 엉뚱한 방에 안읽음을 붙이고, 재연결 후 놓친 메시지가 복구되지 않던 원인)
 */

import { createContext, useContext, type ReactNode } from "react";
import type { Message, MessageType, ReadReceiptsMap, WebSocketMessage } from "@/types/chat";
import { useChatWebSocket } from "@/hooks/useWebSocket";

export interface ChatWebSocketContextValue {
  isConnected: boolean;
  onlineUsers: string[];
  typingUsers: Map<number, Set<string>>;
  readReceipts: ReadReceiptsMap;
  /**
   * 소켓이 열려 있으면 전송하고 true. false 면 호출자가 REST 로 폴백한다.
   * tempId 를 주면 서버 에코 payload 에 client_temp_id 로 돌아와 전송 중 메시지를 정확히 확정할 수 있다.
   */
  sendMessage: (
    roomId: number,
    content: string,
    replyToId?: number,
    messageType?: MessageType,
    tempId?: string
  ) => boolean;
  sendTyping: (roomId: number, isTyping: boolean) => void;
  sendReadReceipt: (roomId: number, messageId: number) => void;
  /**
   * 지금 보고 있는 방(서버가 이 방의 푸시를 억제). 재연결 시 자동으로 다시 알린다.
   * null 이면 해제. 방 전환 시 이전 방은 자동 해제된다.
   */
  setViewingRoom: (roomId: number | null) => void;
  reconnect: () => void;
  disconnect: () => void;
  /** REST 응답으로 받은 메시지(업로드·폴백 전송)를 캐시에 반영 — 소켓 에코와 id 로 중복 제거된다 */
  addMessage: (message: Message & { user_name?: string | null }) => void;
  initializeReadReceipts: (
    roomId: number,
    memberReceipts: { userId: string; lastReadMessageId: number }[]
  ) => void;
  /** 원시 프레임 구독 (전송 중 메시지 확정, 'error' 프레임 처리 등). 반환값은 해제 함수 */
  subscribe: (listener: (frame: WebSocketMessage) => void) => () => void;
}

const ChatWebSocketContext = createContext<ChatWebSocketContextValue | null>(null);

export function ChatWebSocketProvider({ children }: { children: ReactNode }) {
  const wsValue = useChatWebSocket();

  return <ChatWebSocketContext.Provider value={wsValue}>{children}</ChatWebSocketContext.Provider>;
}

export function useSharedChatWebSocket(): ChatWebSocketContextValue {
  const context = useContext(ChatWebSocketContext);
  if (!context) {
    throw new Error("useSharedChatWebSocket must be used within ChatWebSocketProvider");
  }
  return context;
}
